import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, combineLatest, map, startWith, takeUntil } from 'rxjs';
import { ColleagueService } from '../../../core/services/colleague.service';
import { BaseComponent } from '../../../core/components/base/base.component';
import { FeedbackService } from '../../../core/services/feedback.service';
import { MatDialog } from '@angular/material/dialog';

export interface RecipientOption {
  id: string;
  name: string;
  email: string;
  role: string;
  displayName: string;
}

@Component({
  selector: 'app-recipient-search',
  templateUrl: './recipient-search.component.html',
  styleUrls: ['./recipient-search.component.scss']
})
export class RecipientSearchComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() patient: any;
  @Input() placeholder: string = 'Search for recipient...';
  @Input() label: string = 'Recipient';
  @Input() patientOnly: boolean = false;
  @Output() recipientSelected = new EventEmitter<{ name: string, email: string }>();

  recipientControl = new FormControl('');
  filteredOptions!: Observable<RecipientOption[]>;
  allOptions: RecipientOption[] = [];

  constructor(
    private colleagueService: ColleagueService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.initializeOptions();
    this.setupFiltering();
  }

  private initializeOptions(): void {
    // Combine patient data and colleagues
    combineLatest([
      this.colleagueService.colleagues$,
      // Create a simple observable for patient data
      new Observable(observer => observer.next(this.patient))
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([colleagues, patient]) => {
      this.allOptions = [];

      // Add patient data if available
      if (patient) {
        this.addPatientOptions(patient);
      }

      // Add colleagues if available and not in patient-only mode
      if (colleagues && !this.patientOnly) {
        this.addColleagueOptions(colleagues);
      }

      // Remove duplicates based on email
      this.removeDuplicates();
    });
  }

  private addPatientOptions(patient: any): void {
    // Add patient themselves
    if (patient.name && patient.surname && patient.email) {
      this.allOptions.push({
        id: `patient-${patient.id}`,
        name: `${patient.title ? patient.title + ' ' : ''}${patient.name} ${patient.surname}`,
        email: patient.email,
        role: 'Patient',
        displayName: `${patient.title ? patient.title + ' ' : ''}${patient.name} ${patient.surname} (Patient)`
      });
    }

    // Add liable person if available
    if (patient.liableName && patient.liableSurname && patient.liableEmail) {
      this.allOptions.push({
        id: `liable-${patient.id}`,
        name: `${patient.liableTitle ? patient.liableTitle + ' ' : ''}${patient.liableName} ${patient.liableSurname}`,
        email: patient.liableEmail,
        role: 'Person Liable',
        displayName: `${patient.liableTitle ? patient.liableTitle + ' ' : ''}${patient.liableName} ${patient.liableSurname} (Person Liable)`
      });
    }

    // Add emergency contact 1
    if (patient.emergencyContact && patient.emergencyContactEmail) {
      this.allOptions.push({
        id: `emergency1-${patient.id}`,
        name: patient.emergencyContact,
        email: patient.emergencyContactEmail,
        role: 'Emergency Contact',
        displayName: `${patient.emergencyContact} (Emergency Contact)`
      });
    }

    // Add emergency contact 2
    if (patient.emergencyContact2 && patient.emergencyContactEmail2) {
      this.allOptions.push({
        id: `emergency2-${patient.id}`,
        name: patient.emergencyContact2,
        email: patient.emergencyContactEmail2,
        role: 'Emergency Contact 2',
        displayName: `${patient.emergencyContact2} (Emergency Contact 2)`
      });
    }

    // Add medical team members if available and not in patient-only mode
    if (patient.medicalTeam && Array.isArray(patient.medicalTeam) && !this.patientOnly) {
      patient.medicalTeam.forEach((member: any, index: number) => {
        if (member.name && member.surname && member.email) {
          this.allOptions.push({
            id: `medical-team-${patient.id}-${index}`,
            name: `${member.title ? member.title + ' ' : ''}${member.name} ${member.surname}`,
            email: member.email,
            role: member.speciality || 'Medical Team',
            displayName: `${member.title ? member.title + ' ' : ''}${member.name} ${member.surname} (${member.speciality || 'Medical Team'} - Patient Medical Team)`
          });
        }
      });
    }
  }

  private addColleagueOptions(colleagues: any[]): void {
    colleagues.forEach(colleague => {
      if (colleague.name && colleague.surname && colleague.email) {
        this.allOptions.push({
          id: `colleague-${colleague.id}`,
          name: `${colleague.title ? colleague.title + ' ' : ''}${colleague.name} ${colleague.surname}`,
          email: colleague.email,
          role: colleague.speciality || 'Colleague',
          displayName: `${colleague.title ? colleague.title + ' ' : ''}${colleague.name} ${colleague.surname} (${colleague.speciality || 'Colleague'})`
        });
      }
    });
  }

  private removeDuplicates(): void {
    const seen = new Set<string>();
    this.allOptions = this.allOptions.filter(option => {
      // Use name (including title) and email as-is for duplicate detection
      const name = option.name.toLowerCase().trim();
      const email = option.email.toLowerCase().trim();
      const key = `${name}|${email}`;
      
      if (seen.has(key)) {
        return false; // Remove duplicate
      }
      seen.add(key);
      return true; // Keep first occurrence
    });
  }

  private setupFiltering(): void {
    this.filteredOptions = this.recipientControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value))
    );
  }

  private _filter(value: string | RecipientOption | null): RecipientOption[] {
    // Handle case where value is a RecipientOption object (when option is selected)
    if (value && typeof value === 'object') {
      // If it's an object, return empty array (option already selected)
      return [];
    }
    
    // Handle case where value is a string (when user is typing)
    const filterValue = (typeof value === 'string' ? value : '').toLowerCase();
    return this.allOptions.filter(option =>
      option.displayName.toLowerCase().includes(filterValue) ||
      option.email.toLowerCase().includes(filterValue)
    );
  }

  displayFn(option: RecipientOption): string {
    return option ? option.displayName : '';
  }

  onOptionSelected(option: RecipientOption): void {
    this.recipientSelected.emit({
      name: option.name,
      email: option.email
    });
  }

  onInputChange(value: string): void {
    // If user types a custom email, emit it
    if (value && value.includes('@')) {
      this.recipientSelected.emit({
        name: value.split('@')[0], // Use part before @ as name
        email: value
      });
    }
  }

  handleMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  }
}
